/**
 * What each SKIP REASON on the recipient list means, once per distinct reason.
 *
 * ## THE COLUMN THIS REPLACES
 *
 * The list had a "What happened" column that printed a full sentence on every
 * row — the skip reason's, or the message status's. `wend-designer` measured
 * the result: **the suppression caveat alone appeared five times on one page,
 * and twenty-five times the moment somebody filtered to skipped recipients**,
 * where the whole column became 25 rows carrying 4 distinct sentences.
 *
 * For a skipped row the sentence was the badge explained, repeated once per
 * person it applied to: the skip-reason badge sits right there in the Status
 * column, drawn from this same table. So those sentences are drawn here
 * instead, one per distinct reason **on the page of the list that rendered**.
 * No wording changes: forty suppressed recipients get the suppression sentence,
 * in full, once, rather than forty times.
 *
 * ## ONLY THE SKIP HALF CAME HERE, AND THAT IS A REVIEW FINDING
 *
 * The first version moved the message-status sentences here too, keyed on
 * `messageStatus` and labelled with `RECIPIENT_STATUS_DISPLAY[row.status]`.
 * **Those two are not one-to-one**, and a legend keyed on a badge is only
 * honest when the badge settles the sentence.
 *
 * It does not. Two rows both badged `Failed` can be an address Amazon refused
 * (`messageStatus: 'failed'`) and one Amazon accepted whose record was never
 * written (`'unknown'` — the parked case `campaign-send.runner.ts` writes when
 * a worker dies between accepting and recording). Those are opposite claims
 * about whether anything left SES, and the legend emitted **two entries both
 * reading `Failed`**, with nothing on either row saying which was which. A
 * tenant told *"Amazon refused it. Nothing was delivered."* about the parked
 * one sends it again by hand, which is the single thing that runner says must
 * not happen. The same hole swallowed the `nothing yet` state, where `pending`
 * with a claimed message and `pending` with no message at all wear one badge.
 *
 * A skip reason has none of that ambiguity, because it **is** a badge on the
 * row rather than a second field behind one. The message status went back
 * beside the badge it disambiguates, in the Status cell of
 * `CampaignRecipients` — a sentence per row, but only for the rows a badge
 * cannot settle. The measured defect is still fixed: that was the suppression
 * caveat, and it is the half that could honestly move.
 *
 * ## WHY NOT A TOOLTIP, WHICH IS WHAT THE BRIEF ASKED FOR
 *
 * Slice 25's brief says the sentence "becomes that badge's tooltip". This
 * codebase had already decided against that, app-wide, and the reasons hold: a
 * skimmer never opens a tooltip, tooltips do not exist on touch, and they are
 * not reachable from a keyboard. `verify-page-hardening` fails a `title=` on a
 * span anywhere in a screen file. A legend is reachable by everybody, found by
 * Ctrl+F, and read in order by a screen reader.
 *
 * ## WHY PER PAGE RATHER THAN PER CAMPAIGN
 *
 * A legend listing every reason the product has would be documentation. One
 * listing the reasons actually on screen is a key. It is built from the rows
 * that rendered, so it cannot describe a badge the reader cannot see — and it
 * renders nothing at all on a page where nobody was skipped.
 *
 * ## WHY IT IS ITS OWN MODULE
 *
 * `CampaignRecipients` reaches the API client, which reaches `lib/config.ts`,
 * which reads `import.meta.env` and evaluates only inside Vite — so a suite
 * cannot render it and can only assert its source text. An `includes` over a
 * file cannot tell a sentence that is *in* the file from one that *reaches the
 * screen*, and the whole point of this component is which sentences reach the
 * screen and how many times. Extracted, it imports only `@wend/shared`,
 * `@wend/shared` alone now — so
 * `verify-campaign-screens` renders it and counts what comes out. Same reason
 * and same shape as `CampaignFailureSections` and `AttachmentDeliveryNotice`.
 */
import { CAMPAIGN_SKIP_REASONS, type CampaignRecipient } from '@wend/shared';

/**
 * No `channel` argument, and that absence is the fix.
 *
 * It took one so it could call `messageStatusMeaning`, which is channel-aware
 * because since slice 12 a WhatsApp message reaches `delivered` and `failed`
 * like an email one does. Those sentences left — see the header — and a skip
 * reason's sentence is the same on either channel, so there is nothing left
 * here for a channel to change.
 */
export function RecipientBadgeLegend({ rows }: { rows: readonly CampaignRecipient[] }) {
  /*
   * Insertion-ordered and keyed by the reason, so two people skipped for one
   * reason contribute one entry, and the legend reads in the order the reader
   * met the badges.
   */
  const entries = new Map<string, { label: string; sentence: string }>();

  for (const row of rows) {
    if (row.skipReason === null) continue;
    entries.set(row.skipReason, {
      label: CAMPAIGN_SKIP_REASONS[row.skipReason].label,
      sentence: CAMPAIGN_SKIP_REASONS[row.skipReason].sentence,
    });
  }

  if (entries.size === 0) return null;

  return (
    <dl data-role="badge-legend" className="flex flex-col gap-2 border-t border-line pt-4">
      {[...entries].map(([key, entry]) => (
        <div key={key} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <dt className="font-mono text-[0.68rem] uppercase tracking-[0.1em] text-muted">
            {entry.label}
          </dt>
          <dd className="max-w-prose flex-1 text-[0.82rem] leading-snug text-muted">
            {entry.sentence}
          </dd>
        </div>
      ))}
    </dl>
  );
}
